import { resolveTemplate } from '@/content/stories';
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import type { jsPDF } from 'jspdf';
import type { ChildEdition, ArtworkRevision } from '@/contracts/family';
import type { ColoringTemplate, Story } from '@/contracts/content';
import { SCENES, STAGE } from '@/content/scenes';
import { ArtworkService, renderRevision } from './artwork';

/**
 * Keepsake exports (spec §10.4).
 *
 * Rules encoded here:
 *  - Exports are produced from an IMMUTABLE source snapshot — a finished
 *    artwork revision, or an authored scene — never from live editor state.
 *  - Parent exports omit identifying information by default; a nickname is
 *    added only when the parent explicitly asks for it.
 *  - Print quality is generated independently of the on-screen thumbnail:
 *    everything is rasterised at a print scale, not scaled up from a preview.
 *  - Every job reports honest progress and can fail without losing anything,
 *    because nothing here mutates the child's work.
 */

export type ExportKind = 'sketch' | 'png' | 'pdf';

export interface ExportProgress {
  step: string;
  done: number;
  total: number;
}

/**
 * Serialises an authored scene to standalone SVG markup.
 *
 * Rendered through the real DOM and serialised, rather than through
 * `react-dom/server` — the server renderer is ~200 kB that would otherwise sit
 * in every download purely to turn a component into a string.
 */
function sceneToSvg(sceneId: string, width: number, height: number): string {
  const Draw = SCENES[sceneId];
  if (!Draw) throw new Error(`unknown-scene:${sceneId}`);

  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = 'position:fixed;left:-99999px;top:0;width:1px;height:1px;overflow:hidden';
  document.body.appendChild(host);

  const root = createRoot(host);
  try {
    flushSync(() => {
      root.render(
        createElement(
          'svg',
          {
            xmlns: 'http://www.w3.org/2000/svg',
            width,
            height,
            viewBox: `0 0 ${STAGE.w} ${STAGE.h}`,
          },
          createElement(Draw),
        ),
      );
    });
    const el = host.firstElementChild;
    if (!el) throw new Error('scene-render-failed');
    return new XMLSerializer().serializeToString(el);
  } finally {
    root.unmount();
    host.remove();
  }
}

/** Rasterises an authored SVG scene at a print-worthy size. */
export async function renderSceneToPng(sceneId: string, width: number): Promise<string> {
  const height = Math.round((width * STAGE.h) / STAGE.w);
  return rasterise(sceneToSvg(sceneId, width, height), width, height);
}

/**
 * A clean printable sketch: line art only, on white, with no fills.
 * Generated from the template's own paths, so it is genuinely print-clean
 * rather than a screenshot of the editor.
 */
export async function renderPrintableSketch(
  template: ColoringTemplate,
  width = 1600,
): Promise<string> {
  const height = Math.round((width * template.height) / template.width);
  const stroke = Math.max(2, template.width / 200);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" ` +
    `viewBox="0 0 ${template.width} ${template.height}">` +
    `<rect width="100%" height="100%" fill="#FFFFFF"/>` +
    `<path d="${template.outline}" fill="none" stroke="#1B1020" stroke-width="${stroke}" ` +
    `stroke-linejoin="round" stroke-linecap="round"/>` +
    (template.detail ? `<path d="${template.detail}" fill="#1B1020"/>` : '') +
    `</svg>`;
  return rasterise(svg, width, height);
}

function rasterise(svg: string, width: number, height: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas-unavailable');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    // A failed decode is distinct from a network error and must not be
    // reported as a successful export (spec §9.3).
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('svg-decode-failed'));
    };
    img.src = url;
  });
}

export type PaperSize = 'a4' | 'letter';

export interface KeepsakeOptions {
  paper: PaperSize;
  /** Off by default — parent exports omit identifying details (§10.4). */
  includeNickname: boolean;
  nickname?: string;
  onProgress?: (p: ExportProgress) => void;
}

/**
 * Builds the child's personal edition as a PDF: a cover, then every page with
 * the illustration they chose and the story text beneath it.
 */
export async function buildKeepsakePdf(
  account: string,
  story: Story,
  edition: ChildEdition,
  opts: KeepsakeOptions,
): Promise<Blob> {
  const pages = story.currentVersion.pages;
  const total = pages.length + 2;
  let done = 0;
  const tick = (step: string) => {
    done += 1;
    opts.onProgress?.({ step, done, total });
  };

  // Imported on demand so the ~340 kB PDF engine is not in the initial
  // download, on the web or in the iOS bundle.
  const { jsPDF: JsPdf } = await import('jspdf');
  const doc = new JsPdf({ orientation: 'landscape', unit: 'mm', format: opts.paper });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const margin = 16; // generous margins (§10.4)

  const revisionCache = new Map<string, ArtworkRevision>();
  const getRevision = async (id: string) => {
    if (revisionCache.has(id)) return revisionCache.get(id)!;
    const rev = await ArtworkService.getRevision(account, id);
    if (rev) revisionCache.set(id, rev);
    return rev;
  };

  /** A child's picture for a page, re-rendered at print scale. */
  const childPicture = async (revisionId: string) => {
    const rev = await getRevision(revisionId);
    if (!rev || !rev.templateId) return null;
    const tpl = resolveTemplate(story.id, rev.templateId);
    if (!tpl) return null;
    // Rendered from stored vector state at 2x, so print quality does not
    // depend on the on-screen preview (§10.4).
    return renderRevision(account, rev, tpl, { background: '#FFFFFF', scale: 2 });
  };

  /* ------------------------------ Cover ------------------------------ */
  const coverImg = edition.coverRevisionId
    ? await childPicture(edition.coverRevisionId)
    : await renderSceneToPng(story.coverSceneId, 1600);

  if (coverImg) {
    drawContained(doc, coverImg, margin, margin, pw - margin * 2, ph - margin * 2 - 26);
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.setTextColor(44, 28, 56);
  doc.text(story.title, pw / 2, ph - margin - 8, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(120, 104, 134);
  doc.text(
    opts.includeNickname && opts.nickname ? `A copy made by ${opts.nickname}` : 'A personal copy',
    pw / 2,
    ph - margin,
    { align: 'center' },
  );
  tick('Cover');

  /* ------------------------------ Pages ------------------------------ */
  for (const page of pages) {
    doc.addPage();
    const revisionId = edition.slots[page.id];
    let img: string | null = null;
    let personal = false;
    if (revisionId) {
      img = await childPicture(revisionId);
      personal = !!img;
    }
    if (!img) img = await renderSceneToPng(page.sceneId, 1600);

    const textBlockH = 30;
    drawContained(doc, img, margin, margin, pw - margin * 2, ph - margin * 2 - textBlockH);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(15);
    doc.setTextColor(44, 28, 56);
    const lines = doc.splitTextToSize(page.text, pw - margin * 2);
    doc.text(lines, pw / 2, ph - margin - textBlockH + 20, { align: 'center' });

    if (personal) {
      doc.setFontSize(9);
      doc.setTextColor(160, 146, 176);
      doc.text('picture by the reader', pw - margin, ph - margin + 2, { align: 'right' });
    }
    tick(`Page ${page.sequence + 1}`);
  }

  /* ------------------------------ Colophon ------------------------------ */
  doc.addPage();
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(120, 104, 134);
  doc.text(
    [
      'Story, illustrations and line art are original works created for e(x)amine.',
      'The coloured pictures in this book were made by the reader.',
      'Printed from a personal copy. The original story is unchanged.',
    ],
    pw / 2,
    ph / 2,
    { align: 'center' },
  );
  tick('Finishing');

  return doc.output('blob');
}

/** Draws an image inside a box, preserving its aspect ratio. */
function drawContained(
  doc: jsPDF,
  dataUrl: string,
  x: number,
  y: number,
  boxW: number,
  boxH: number,
) {
  const props = doc.getImageProperties(dataUrl);
  const ratio = Math.min(boxW / props.width, boxH / props.height);
  const w = props.width * ratio;
  const h = props.height * ratio;
  doc.addImage(dataUrl, 'PNG', x + (boxW - w) / 2, y + (boxH - h) / 2, w, h, undefined, 'FAST');
}

/** Converts a data URL to a Blob for the share/download adapter. */
export function dataUrlToBlob(dataUrl: string): Blob {
  const [head, body] = dataUrl.split(',');
  const mime = /:(.*?);/.exec(head)?.[1] ?? 'image/png';
  const bin = atob(body);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}
